import { requestIdleCallback } from '@theme/utilities';

const IMAGE_SELECTOR = 'img[data-image-loading-bar]:not([data-image-loading-bar="false"])';
const HOST_SELECTOR = '[data-image-loading-host]';
const HOST_LOADING_CLASS = 'is-image-loading';
const IMAGE_LOADED_CLASS = 'is-image-loaded';

/** @type {WeakSet<HTMLImageElement>} */
const boundImages = new WeakSet();

/**
 * @param {HTMLImageElement} image
 * @returns {HTMLElement | null}
 */
function getHost(image) {
  const host = image.closest(HOST_SELECTOR);
  return host instanceof HTMLElement ? host : null;
}

/**
 * @param {HTMLElement} host
 */
function syncHost(host) {
  const pending = host.querySelector(`${IMAGE_SELECTOR}:not(.${IMAGE_LOADED_CLASS})`);
  host.classList.toggle(HOST_LOADING_CLASS, Boolean(pending));
}

/**
 * @param {HTMLImageElement} image
 */
function finish(image) {
  image.classList.add(IMAGE_LOADED_CLASS);
  const host = getHost(image);
  if (host) syncHost(host);
}

/**
 * @param {HTMLImageElement} image
 */
function start(image) {
  const host = getHost(image);
  if (!host) return;

  if (image.complete || image.classList.contains(IMAGE_LOADED_CLASS)) {
    finish(image);
    return;
  }

  image.classList.remove(IMAGE_LOADED_CLASS);
  syncHost(host);
}

/**
 * @param {HTMLImageElement} image
 */
function bindImage(image) {
  if (boundImages.has(image)) {
    start(image);
    return;
  }

  boundImages.add(image);

  const onDone = () => {
    requestIdleCallback(() => finish(image), { timeout: 150 });
  };

  image.addEventListener('load', onDone);
  image.addEventListener('error', onDone);

  new MutationObserver(() => {
    image.classList.remove(IMAGE_LOADED_CLASS);
    start(image);
  }).observe(image, {
    attributes: true,
    attributeFilter: ['src', 'srcset'],
  });

  start(image);
}

/**
 * @param {ParentNode} root
 */
function bindImages(root) {
  if (root instanceof HTMLImageElement && root.matches(IMAGE_SELECTOR)) {
    bindImage(root);
    return;
  }

  root.querySelectorAll(IMAGE_SELECTOR).forEach((node) => {
    if (node instanceof HTMLImageElement) bindImage(node);
  });
}

bindImages(document);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) bindImages(node);
    }
  }
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
